import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, tap, throwError } from "rxjs";
import { SessionService } from "@app/shared/auth/services/session.service";

const UNAUTHORIZED_PATH = "/auth/unauthorized";
const GRAPHQL_UNAUTHORIZED_CODES = new Set(["UNAUTHENTICATED", "UNAUTHORIZED", "FORBIDDEN"]);

/** Subset of a GraphQL error shape we care about for 401 detection. */
interface IGraphqlError {
  extensions?: {
    code?: string;
    formatted?: {
      code?: number;
      type?: string;
    };
  };
}

/**
 * Returns true if the error is an authentication/authorization failure.
 *
 * Handles two shapes:
 *   - `extensions.code` ∈ {UNAUTHENTICATED, UNAUTHORIZED, FORBIDDEN} (Apollo default)
 *   - `extensions.formatted.code === 401` or `extensions.formatted.type === "UNAUTHORIZED"`
 *     (kernel framework envelope — outer code is `INTERNAL_SERVER_ERROR`)
 */
function isUnauthorizedError(error: IGraphqlError | undefined): boolean {
  if (!error?.extensions) return false;

  const outerCode = error.extensions.code;
  if (outerCode && GRAPHQL_UNAUTHORIZED_CODES.has(outerCode)) return true;

  const formatted = error.extensions.formatted;
  if (!formatted) return false;
  if (formatted.code === 401) return true;
  if (formatted.type && GRAPHQL_UNAUTHORIZED_CODES.has(formatted.type)) return true;

  return false;
}

/** A single GraphQL result envelope (one entry inside a batched response). */
interface IGraphqlResult {
  errors?: IGraphqlError[];
}

/**
 * Catches authentication failures from both transports the app uses:
 *   - HTTP 401 (any plain HTTP request)
 *   - GraphQL 200 OK with a 401-shaped error in `errors[].extensions`
 *     (the kernel uses `HttpBatchLink`, so the body is an array of results)
 *
 * On a match: wipe all kiosk session state and redirect to /auth/unauthorized.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const sessionService = inject(SessionService);

  const handleUnauthorized = (): void => {
    sessionService.reset();
    if (router.url !== UNAUTHORIZED_PATH) {
      router.navigateByUrl(UNAUTHORIZED_PATH);
    }
  };

  return next(req).pipe(
    tap(event => {
      if (!(event instanceof HttpResponse)) return;
      // HttpBatchLink wraps results in an array; a plain HttpLink uses a single object.
      const body = event.body as IGraphqlResult | IGraphqlResult[] | null;
      const results = Array.isArray(body) ? body : body ? [body] : [];
      if (results.some(result => result.errors?.some(isUnauthorizedError))) {
        handleUnauthorized();
      }
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        handleUnauthorized();
      }
      return throwError(() => error);
    })
  );
};
